/**
 * Lightweight A/B test utility.
 * Variant assignment is persisted in localStorage for cross-session consistency.
 */

const STORAGE_PREFIX = 'ab_test_';

const TESTS = {
  results_view: {
    variants: ['control', 'swipe'],
    weights: [0.5, 0.5],
  },
};

/**
 * Get (or assign) the variant for a given test.
 * First call assigns randomly; all subsequent calls return the stored value.
 */
export function getVariant(testName) {
  const key = STORAGE_PREFIX + testName;

  try {
    const stored = localStorage.getItem(key);
    if (stored) return stored;
  } catch {
    // localStorage unavailable (SSR, private browsing) — fall through to assign
  }

  const test = TESTS[testName];
  if (!test) {
    console.warn(`[A/B] Unknown test: ${testName}`);
    return 'control';
  }

  // Weighted random assignment
  const roll = Math.random();
  let cumulative = 0;
  let variant = test.variants[test.variants.length - 1]; // fallback to last
  for (let i = 0; i < test.variants.length; i++) {
    cumulative += test.weights[i];
    if (roll < cumulative) {
      variant = test.variants[i];
      break;
    }
  }

  try {
    localStorage.setItem(key, variant);
  } catch {
    // Silent fail — variant still returned for this session
  }

  return variant;
}

/**
 * Returns a map of all active test assignments for this user.
 * Useful for attaching to every analytics event.
 */
export function getActiveTests() {
  const active = {};
  for (const testName of Object.keys(TESTS)) {
    const key = STORAGE_PREFIX + testName;
    try {
      const stored = localStorage.getItem(key);
      if (stored) active[testName] = stored;
    } catch {
      // skip
    }
  }
  return active;
}
